import { ImportDataLegalEntitiesQuery } from '@/__generated__/graphql';
import Button from '@/components/basicComponents/button';
import Select from '@/components/basicComponents/select';
import { IEntityCodesTableData } from '@/components/settings-page/importData/types';
import NoResults from '@/components/table/noResults';
import { TableRowCell, TableRowWrapper, TextCell } from '@/components/table/styles';
import styled from 'styled-components';
import { columns } from '../constants';
import { RowStatus } from './cell/rowStatus';

const getAssignmentSelectOptionTooltip = (entity: ImportDataLegalEntitiesQuery['LegalEntities'][number]): React.ReactNode => {
  return (
    <AssignmentSelectOptionTooltipWrapper>
      {entity?.family?.name && <div>Household: {entity.family.name}</div>}
      {entity?.id && <div>Entity Static ID: {entity.id}</div>}
      {entity?.advisorCRMId && <div>Entity CRM ID: {entity.advisorCRMId}</div>}
    </AssignmentSelectOptionTooltipWrapper>
  );
};

interface TableBodyProps {
  tableData: IEntityCodesTableData[];
  sortedLegalEntities: ImportDataLegalEntitiesQuery['LegalEntities'];
  legalEntitiesLoading: boolean;
  handleChangeAssignment: (entityId: string, prevValue: string, entityCode: string) => void;
  resetAssignment: (entityCode: string, prevValue: string) => void;
}

const TableBody = ({ tableData, sortedLegalEntities, legalEntitiesLoading, handleChangeAssignment, resetAssignment }: TableBodyProps) => {
  if (!tableData?.length) {
    return <NoResults />;
  }

  return (
    <>
      {tableData.map((row) => (
        <CustomTableRowWrapper key={row.id}>
          <TableRowCell width={columns[0].width}>
            <TextCell>{row.entityCode}</TextCell>
          </TableRowCell>
          <TableRowCell width={columns[1].width}>
            <TextCell>{row.entityName}</TextCell>
          </TableRowCell>
          <TableRowCell width={columns[2].width}>
            {row.entityStatus === 'Assigned' ? (
              <TextCell>{row.entityAssignment}</TextCell>
            ) : (
              <Select
                value={row.entityAssignment}
                previewValue={row.entityAssignment === 'Select An Entity' ? '' : row.entityAssignment}
                setValue={(value) => handleChangeAssignment(value, row.entityAssignment, row.entityCode)}
                options={sortedLegalEntities.map((entity) => ({
                  value: entity.id,
                  label: `${entity.entityName} (${entity.tenant.name})`,
                  tooltip: getAssignmentSelectOptionTooltip(entity)
                }))}
                loading={legalEntitiesLoading}
                size="md"
                additionalOption={
                  <ClearSelectionButtonWrapper>
                    <Button
                      size="sm"
                      onClick={() => resetAssignment(row.entityCode, row.entityAssignment)}
                      styleType={row.entityAssignment === 'Select An Entity' ? 'disabled' : 'outline'}
                    >
                      Clear Selection
                    </Button>
                  </ClearSelectionButtonWrapper>
                }
                withSearch
              />
            )}
          </TableRowCell>
          <TableRowCell width={columns[3].width}>
            <TextCell>
              <RowStatus status={row.entityStatus} />
            </TextCell>
          </TableRowCell>
        </CustomTableRowWrapper>
      ))}
    </>
  );
};

export default TableBody;

const CustomTableRowWrapper = styled(TableRowWrapper)`
  justify-content: space-between;
`;

const AssignmentSelectOptionTooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClearSelectionButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
